/** The save was refused because it would change a pack-managed aspect.
 *
 *  A plain "save failed" is the wrong answer here: the edit is valid, the
 *  notification is simply not the editor's to change yet. The banner names the
 *  aspect, names the field that tripped it, and offers the one action that
 *  resolves it — with the consequence stated, because unmanaging is what stops
 *  the pack maintaining that aspect from then on.
 */
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Alert, AlertTitle, Button, Stack, Typography } from '@mui/material';

import { MANAGED_LABELS, type ManagedAspectKey, type ManagedRefusal } from '@/api/cannedNotifications';

const LABEL: Record<ManagedAspectKey, string> = Object.fromEntries(
  MANAGED_LABELS.map((m) => [m.key, m.label]),
) as Record<ManagedAspectKey, string>;

interface Props {
  refusal: ManagedRefusal;
  canManage: boolean;
  onUnmanage: (aspects: ManagedAspectKey[]) => void;
  onDismiss: () => void;
}

export function ManagedRefusalBanner({ refusal, canManage, onUnmanage, onDismiss }: Props) {
  const aspects = Array.from(new Set(refusal.managed.map((m) => m.aspect)));
  const names = aspects.map((a) => LABEL[a] ?? a).join(' and ');

  return (
    <Alert severity="warning" onClose={onDismiss} sx={{ mb: 2 }}>
      <AlertTitle>Not saved — {names.toLowerCase()} is managed by the notification pack</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {refusal.detail}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        Refused: {refusal.managed.map((m) => m.field).join(', ')}
      </Typography>
      {canManage && (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LockOpenIcon />}
            onClick={() => onUnmanage(aspects)}
          >
            Unmanage {names.toLowerCase()} and keep my edit
          </Button>
        </Stack>
      )}
    </Alert>
  );
}
